import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import DashboardStep from './steps/DashboardStep';
import ReviewDataStep from './steps/ReviewDataStep';
import TargetingGapAnalysisStep from './steps/TargetingGapAnalysisStep';
import GapAnalysisStep from './steps/GapAnalysisStep';
import ExperienceBuilderStep from './steps/ExperienceBuilderStep';
import CareerRoadmapStep from './steps/CareerRoadmapStep';
import EnhanceStep from './steps/EnhanceStep';
import PreviewStep from './steps/PreviewStep';
import ResultsStep from './steps/ResultsStep';
import Layout from '../../NewDashboard/Layout';
import { saveResumeProgress, getLatestResume } from '../../../services/resume.service';

const ResumeBuilder = () => {
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [appState, setAppState] = useState({
    uploadedFile: location.state?.uploadedFile || null,
    reviewedData: location.state?.reviewedData || null,
    targetRole: location.state?.targetRole || '',
    jobDescription: location.state?.jobDescription || '',
    additionalInfo: location.state?.additionalInfo || '',
    gapAnalysis: location.state?.gapAnalysis || null,
    experienceEntries: {},
    selectedExperiences: [],
    currentExperienceIndex: 0,
    resume_template_id: 'classic-ats',
    resume_photo_b64: null,
    result: location.state?.result || null,
    type: location.state?.type || 'create',
    originalScore: location.state?.originalScore || null,
    hasCareerRoadmap: location.state?.hasCareerRoadmap || false,
    stage: location.state?.stage || 1,
    history: ['dashboard']
  });

  const saveProgress = async (step, state) => {
    if (step === 'dashboard' || step === 'upload' || loading) return;
    
    const userId = localStorage.getItem('user_id');
    const category = localStorage.getItem('category');
    if (!userId || !category) return;
    
    try {
      await saveResumeProgress(userId, category, state.stage || 1, step, state);
    } catch(err) {
      console.error('Failed to save progress:', err);
    }
  };

  useEffect(() => {
    const fetchProgress = async () => {
      const userId = localStorage.getItem('user_id');
      const category = localStorage.getItem('category');
      if (!userId || !category) {
        setLoading(false);
        return;
      }

      try {
        const response = await getLatestResume(userId, category);
        if (response.success && response.resume) {
          const { step, parsed_data, target_role, job_description, gap_analysis, updated_resume_url, stage } = response.resume;
          const data = typeof parsed_data === 'string' ? JSON.parse(parsed_data) : parsed_data;
          const safeData = data || {};
          
          // Reconstruct result object for compatibility with PreviewStep/ResultsStep
          const synthesizedResult = {
            id: response.resume.id,
            resume_id: response.resume.id,
            downloadUrl: updated_resume_url || safeData.result?.downloadUrl || '',
            resumeData: {
              name: safeData.name,
              email: safeData.email,
              phone: safeData.phone,
              location: safeData.location,
              linkedin: safeData.linkedin,
              github: safeData.github,
              summary: safeData.summary,
              experience: safeData.experience,
              education: safeData.education,
              skills: safeData.skills,
              projects: safeData.projects || safeData.leadership,
              certifications: safeData.certifications,
              interests: safeData.interests,
              languages: safeData.languages
            },
            fileName: updated_resume_url ? updated_resume_url.split('/').pop() : 'resume.pdf'
          };

          setAppState(prev => ({
            ...prev,
            ...safeData,
            result: (step === 'preview' || step === 'results') ? synthesizedResult : (safeData.result || prev.result),
            uploadedFile: response.resume,
            targetRole: target_role || safeData.targetRole || safeData.TargetRole || prev.targetRole,
            jobDescription: job_description || safeData.jobDescription || safeData.JobDescription || prev.jobDescription,
            gapAnalysis: gap_analysis ? (typeof gap_analysis === 'string' ? JSON.parse(gap_analysis) : gap_analysis) : (safeData.gapAnalysis || prev.gapAnalysis),
            history: safeData.history || prev.history,
            stage: stage || safeData.stage || 1
          }));
          
        }
      } catch (err) {
        console.error('Failed to fetch resume progress:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchProgress();
  }, []);

  useEffect(() => {
    if (loading) return;
    const path = location.pathname.split('/').pop();
    const validSteps = ['dashboard', 'upload', 'review', 'enhance', 'preview', 'results', 'create', 'gap-analysis', 'experience-builder', 'career-roadmap'];
    
    if (validSteps.includes(path)) {
      const stepMap = {
        'resume-builder': 'dashboard',
        'upload': 'dashboard',
        'create': 'targeting-gap',
        'targeting-gap': 'targeting-gap'
      };
      
      const mappedStep = stepMap[path] || path;
      setCurrentStep(mappedStep);
    }
  }, [location.pathname]);

  const handleNavigate = (step, data = {}) => {
    if (step === -1) {
      // Simple back navigation
      if (appState.history.length > 1) {
        const newHistory = [...appState.history];
        newHistory.pop();
        const prevStep = newHistory[newHistory.length - 1];
        
        const newState = { ...appState, history: newHistory };
        setCurrentStep(prevStep);
        setAppState(newState);
        saveProgress(prevStep, newState);
      }
      return;
    }

    const activeHistory = data.history || appState.history;
    const newHistory = [...activeHistory];
    
    if (newHistory[newHistory.length - 1] !== step) {
      newHistory.push(step);
    }

    // Baseline state for fresh uploads to prevent data leakage from previous sessions
    const baselineState = data.isFresh ? {
      uploadedFile: null,
      reviewedData: null,
      targetRole: '',
      jobDescription: '',
      additionalInfo: '',
      gapAnalysis: null,
      experienceEntries: {},
      selectedExperiences: [],
      currentExperienceIndex: 0,
      resume_template_id: 'classic-ats',
      resume_photo_b64: null,
      result: null,
      type: 'create',
      originalScore: null,
      hasCareerRoadmap: false,
    } : appState;

    const newState = {
      ...baselineState,
      ...data,
      history: newHistory
    };

    setCurrentStep(step);
    setAppState(newState);
    saveProgress(step, newState);
  };

  const handleDataUpdate = (data) => {
    setAppState(prev => {
      const newState = { ...prev, ...data };
      saveProgress(currentStep, newState);
      return newState;
    });
  };

  const renderStep = () => {
    switch (currentStep) {
      case 'dashboard':
        return (
          <DashboardStep
            onNavigate={handleNavigate}
            onDataUpdate={handleDataUpdate}
          />
        );
      case 'review':
        return (
          <ReviewDataStep
            uploadedFile={appState.uploadedFile}
            reviewedData={appState.reviewedData}
            onNavigate={handleNavigate}
            onDataUpdate={handleDataUpdate}
          />
        );
      case 'targeting-gap':
        return (
          <TargetingGapAnalysisStep
            uploadedFile={appState.uploadedFile}
            reviewedData={appState.reviewedData}
            targetRole={appState.targetRole}
            jobDescription={appState.jobDescription}
            existingAnalysis={appState.gapAnalysis}
            onNavigate={handleNavigate}
            onDataUpdate={handleDataUpdate}
          />
        );
      case 'gap-analysis':
        return (
          <GapAnalysisStep
            uploadedFile={appState.uploadedFile}
            reviewedData={appState.reviewedData}
            targetRole={appState.targetRole}
            jobDescription={appState.jobDescription}
            additionalInfo={appState.additionalInfo}
            gapAnalysis={appState.gapAnalysis}
            selectedExperiences={appState.selectedExperiences}
            onNavigate={handleNavigate}
            onDataUpdate={handleDataUpdate}
          />
        );
      case 'experience-builder':
        return (
          <ExperienceBuilderStep
            key={appState.currentExperienceIndex}
            uploadedFile={appState.uploadedFile}
            reviewedData={appState.reviewedData}
            targetRole={appState.targetRole}
            jobDescription={appState.jobDescription}
            additionalInfo={appState.additionalInfo}
            gapAnalysis={appState.gapAnalysis}
            experienceEntries={appState.experienceEntries}
            selectedExperiences={appState.selectedExperiences}
            currentExperienceIndex={appState.currentExperienceIndex}
            onNavigate={handleNavigate}
            onDataUpdate={handleDataUpdate}
          />
        );
      case 'career-roadmap':
        return (
          <CareerRoadmapStep
            uploadedFile={appState.uploadedFile}
            reviewedData={appState.reviewedData}
            targetRole={appState.targetRole}
            jobDescription={appState.jobDescription}
            additionalInfo={appState.additionalInfo}
            gapAnalysis={appState.gapAnalysis}
            onNavigate={handleNavigate}
            onDataUpdate={handleDataUpdate}
          />
        );
      case 'enhance':
        return (
          <EnhanceStep
            uploadedFile={appState.uploadedFile}
            onNavigate={handleNavigate}
            onDataUpdate={handleDataUpdate}
          />
        );
      case 'preview':
        return (
          <PreviewStep
            result={appState.result}
            type={appState.type}
            targetRole={appState.targetRole}
            gapAnalysis={appState.gapAnalysis}
            resume_template_id={appState.resume_template_id}
            resume_photo_b64={appState.resume_photo_b64}
            uploadedFile={appState.uploadedFile}
            reviewedData={appState.reviewedData}
            onNavigate={handleNavigate}
            onDataUpdate={handleDataUpdate}
          />
        );
      case 'results':
        return (
          <ResultsStep
            result={appState.result}
            type={appState.type}
            targetRole={appState.targetRole}
            originalScore={appState.originalScore}
            uploadedFile={appState.uploadedFile}
            reviewedData={appState.reviewedData}
            onNavigate={handleNavigate}
            onDataUpdate={handleDataUpdate}
          />
        );
      default:
        return <DashboardStep onNavigate={handleNavigate} onDataUpdate={handleDataUpdate} />;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
          <div className="w-12 h-12 border-4 border-blue-light border-t-blue rounded-full animate-spin"></div>
          <p className="text-slate-500 font-medium animate-pulse">Resuming your progress...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="resume-tool-wrapper overflow-x-hidden">
        {renderStep()}
      </div>
    </Layout>
  );
};

export default ResumeBuilder;
